import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  selectTransactionDetail,
  selectTransactionDetailComplements,
  selectTransactionDetailError
} from 'src/store/selectors/transactionSelectors';
import {
  LOAD_TRANSACTION_DETAIL,
  loadTransactionDetail,
  clearTransactionDetail,
  assignTransactionToContract
} from 'src/store/actions/transactionActions';
import { PaymentMethod } from 'src/utils/constants/cfdi';
import StatusBadge from 'src/components/StatusBadge';
import SkeletonBlock from 'src/components/SkeletonBlock';
import DocumentViewerDialog from 'src/components/DocumentViewer/DocumentViewerDialog';
import SendEmailDialog from 'src/views/taxAccounting/CFDIIssued/SendEmailDialog';
import AssignTransactionToContractDialog from '../AssignTransactionToContractDialog';
import InvoiceSummaryStats from './InvoiceSummaryStats';
import InvoiceFields from './InvoiceFields';
import PaymentComplementsList from './PaymentComplementsList';
import {
  MUTED_LABEL_COLOR,
  HEADING_TEXT_COLOR,
  HAIRLINE_BORDER_COLOR,
  CONTENT_CANVAS_COLOR
} from './styleConstants';

const PAYMENT_STATUS_TONE = {
  PAID: 'success',
  PARTIAL: 'info',
  PENDING: 'warning'
};

const CARD_SX = {
  border: '1px solid #e0e5ec',
  borderRadius: '4px',
  marginBottom: '18px',
  backgroundColor: '#ffffff'
};

const CARD_HEAD_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: '64px',
  padding: '0 20px',
  borderBottom: `1px solid ${HAIRLINE_BORDER_COLOR}`
};

const CARD_TITLE_SX = {
  color: HEADING_TEXT_COLOR,
  fontSize: '17px',
  fontWeight: 800
};

const InvoiceDetailDrawer = ({
  open,
  invoiceId,
  cutoffDate,
  onClose,
  onMutated
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const detail = useSelector(selectTransactionDetail);
  const complements = useSelector(selectTransactionDetailComplements);
  const hasError = useSelector(selectTransactionDetailError);
  const loading = useSelector(
    isLoadingSelector([LOAD_TRANSACTION_DETAIL.action])
  );

  const [viewingDocument, setViewingDocument] = useState(null);
  const [sendingEmailForInvoiceId, setSendingEmailForInvoiceId] =
    useState(null);
  const [assigningContractForId, setAssigningContractForId] = useState(null);

  useEffect(() => {
    if (open && invoiceId) {
      dispatch(loadTransactionDetail(invoiceId, cutoffDate));
    }
    if (!open) {
      dispatch(clearTransactionDetail());
    }
  }, [dispatch, open, invoiceId, cutoffDate]);

  const complementsById = useMemo(() => {
    const byId = {};
    complements.forEach((complement) => {
      byId[complement.id] = complement;
    });
    return byId;
  }, [complements]);

  const handleRetry = () => {
    dispatch(loadTransactionDetail(invoiceId, cutoffDate));
  };

  const refresh = () => {
    handleRetry();
    onMutated();
  };

  const handleViewPdf = (id) => {
    const source = detail && id === detail.id ? detail : complementsById[id];
    if (source?.document?.documentUrl) {
      setViewingDocument({
        documentUrl: source.document.documentUrl,
        fileName: source.folio
      });
    }
  };

  const handleSendToClient = (id) => {
    const source = detail && id === detail.id ? detail : complementsById[id];
    if (source?.invoiceDbId) {
      setSendingEmailForInvoiceId(source.invoiceDbId);
    }
  };

  const handleAssociateContract = (id) => {
    setAssigningContractForId(id);
  };

  const handleAssignInvoiceToContract = (values) => {
    dispatch(
      assignTransactionToContract(assigningContractForId, values, () => {
        setAssigningContractForId(null);
        refresh();
      })
    );
  };

  const complementCountLabel =
    complements.length === 1
      ? `1 ${t('TRANSACTION.COMPLEMENT_SINGULAR')}`
      : `${complements.length} ${t('TRANSACTION.COMPLEMENT_PLURAL')}`;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      PaperProps={{
        sx: {
          width: 680,
          fontFamily: (theme) => theme.typography.fontFamily
        }
      }}
    >
      <Box
        sx={{
          padding: '28px 30px',
          position: 'relative',
          minHeight: '100%',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label={t('COMMON.CLOSE')}
          sx={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            border: (theme) => `1px solid ${theme.palette.primary.main}`,
            color: 'primary.main'
          }}
        >
          <CloseIcon />
        </IconButton>

        {hasError ? (
          <Box sx={{ display: 'grid', gap: '14px', paddingTop: '8px' }}>
            <Typography color="error">
              {t('TRANSACTION.DETAIL.LOAD_ERROR')}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleRetry}
              sx={{ justifySelf: 'start' }}
            >
              {t('TRANSACTION.DETAIL.RETRY')}
            </Button>
          </Box>
        ) : loading || !detail ? (
          <Box sx={{ display: 'grid', gap: '18px', paddingTop: '8px' }}>
            <SkeletonBlock width="short" />
            <SkeletonBlock width="medium" tall />
            <SkeletonBlock width="medium" />
          </Box>
        ) : (
          <>
            <Typography
              sx={{
                color: MUTED_LABEL_COLOR,
                fontSize: '12px',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}
            >
              {t('TRANSACTION.DETAIL.TITLE')}
            </Typography>
            <Typography
              sx={{
                margin: '7px 0 12px',
                color: HEADING_TEXT_COLOR,
                fontSize: '30px',
                fontWeight: 800
              }}
            >
              {detail.folio}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                marginBottom: '18px'
              }}
            >
              {detail.paymentMethod && (
                <StatusBadge
                  tone={
                    detail.paymentMethod === PaymentMethod.PPD
                      ? 'info'
                      : 'neutral'
                  }
                >
                  {detail.paymentMethod}
                </StatusBadge>
              )}
              {detail.paymentStatus && (
                <StatusBadge tone={PAYMENT_STATUS_TONE[detail.paymentStatus]}>
                  {t(`TRANSACTION.PAYMENT_STATUS.${detail.paymentStatus}`)}
                </StatusBadge>
              )}
            </Box>
            <Divider sx={{ marginX: '-30px' }} />

            <Box
              sx={{
                backgroundColor: CONTENT_CANVAS_COLOR,
                marginX: '-30px',
                marginBottom: '-28px',
                padding: '18px 30px 28px',
                flex: 1
              }}
            >
              <InvoiceSummaryStats
                amountIvaIncluded={detail.amountIvaIncluded}
                pendingAmount={detail.pendingAmount}
              />

              <Box sx={CARD_SX}>
                <Box sx={CARD_HEAD_SX}>
                  <Typography sx={CARD_TITLE_SX}>
                    {t('TRANSACTION.DETAIL.INVOICE_DATA')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileOpenIcon />}
                      disabled={!detail.document?.documentUrl}
                      onClick={() => handleViewPdf(detail.id)}
                    >
                      {t('TRANSACTION.DETAIL.VIEW_INVOICE_PDF')}
                    </Button>
                    {!detail.contractId && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PublishedWithChangesIcon />}
                        onClick={() => handleAssociateContract(detail.id)}
                      >
                        {t('TRANSACTION.ASSOCIATE_TRANSACTION_TO_CONTRACT')}
                      </Button>
                    )}
                  </Box>
                </Box>
                <InvoiceFields
                  client={detail.client}
                  contractFolioWithoutPrefix={detail.contractFolioWithoutPrefix}
                  date={detail.date}
                  origin={detail.origin}
                  categoryDescription={detail.categoryDescription}
                  comment={detail.comment}
                />
              </Box>

              <Box sx={{ ...CARD_SX, marginBottom: 0 }}>
                <Box sx={CARD_HEAD_SX}>
                  <Typography sx={CARD_TITLE_SX}>
                    {t('TRANSACTION.DETAIL.REGISTERED_PAYMENTS')}
                  </Typography>
                  <StatusBadge tone="neutral">
                    {complementCountLabel}
                  </StatusBadge>
                </Box>
                <PaymentComplementsList
                  complements={complements}
                  onViewPdf={handleViewPdf}
                  onSendToClient={handleSendToClient}
                  onAssociateContract={handleAssociateContract}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>

      {viewingDocument && (
        <DocumentViewerDialog
          open={Boolean(viewingDocument)}
          onClose={() => setViewingDocument(null)}
          documentUrl={viewingDocument.documentUrl}
          fileName={viewingDocument.fileName}
        />
      )}

      {sendingEmailForInvoiceId && detail && (
        <SendEmailDialog
          open={Boolean(sendingEmailForInvoiceId)}
          onClose={() => setSendingEmailForInvoiceId(null)}
          id={sendingEmailForInvoiceId}
          clientId={detail.clientId}
        />
      )}

      {assigningContractForId && detail && (
        <AssignTransactionToContractDialog
          open={Boolean(assigningContractForId)}
          transaction={{ clientId: detail.clientId }}
          handleAssignInvoiceToContract={handleAssignInvoiceToContract}
          handleClose={() => setAssigningContractForId(null)}
        />
      )}
    </Drawer>
  );
};

InvoiceDetailDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  invoiceId: PropTypes.number,
  cutoffDate: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onMutated: PropTypes.func.isRequired
};

InvoiceDetailDrawer.defaultProps = {
  invoiceId: null,
  cutoffDate: null
};

export default InvoiceDetailDrawer;
